import { useState, useEffect } from 'react';
import { getIncentiveTransactions, createIncentiveTransaction, updateIncentiveTransaction, deleteIncentiveTransaction, splitIncentiveTransaction, getIncentiveSummary, getIncentiveConfig, updateIncentiveConfig, getClients, getUsers } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatApiError, formatCurrency, getClientDisplayName } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { SearchableSelect } from '../components/ui/searchable-select';
import { DatePicker } from '../components/ui/date-picker';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { Plus, Check, Clock, ArrowClockwise, TrendUp, Target, CurrencyInr, X, Trash, Warning, Pencil, Phone, VideoCamera, CalendarCheck, ClipboardText, UsersThree, ArrowsSplit } from '@phosphor-icons/react';

const CATEGORIES = [
  { value: 'Mutual Fund', label: 'Mutual Fund' },
  { value: 'Health Insurance', label: 'Health Insurance' },
  { value: 'Term Insurance', label: 'Term Insurance' },
];
const FUND_TYPES = [
  { value: 'Debt Fund', label: 'Debt Fund' },
  { value: 'Hybrid', label: 'Hybrid' },
  { value: 'Equity Fund', label: 'Equity Fund' },
];
const TXN_NATURES = [
  { value: 'Financial', label: 'Financial Transaction' },
  { value: 'Non Financial', label: 'Non Financial Transaction' },
];
const TXN_TYPES = [
  { value: 'SIP', label: 'SIP' },
  { value: 'Purchase', label: 'Purchase' },
  { value: 'Broker Change', label: 'Broker Change' },
];
const CLIENT_FLAGS = [
  { value: 'New Client', label: 'New Client' },
  { value: 'Existing Client', label: 'Existing Client' },
];
const STATUSES = [
  { value: 'Pending Approval', label: 'Pending Approval' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Paid', label: 'Paid' },
  { value: 'Clawback', label: 'Clawback' },
];

const statusColors = {
  'Pending Approval': 'bg-amber-100 text-amber-700',
  'Approved': 'bg-blue-100 text-blue-700',
  'Rejected': 'bg-red-100 text-red-700',
  'Paid': 'bg-green-100 text-green-700',
  'Clawback': 'bg-red-100 text-red-700',
};

export default function IncentivesPage() {
  const { user, isAdmin } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [config, setConfig] = useState(null);
  const [clients, setClients] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [viewingTxn, setViewingTxn] = useState(null);
  const [editingTxn, setEditingTxn] = useState(null);
  const [splittingTxn, setSplittingTxn] = useState(null); // { ...txn, splits: [{user_id, percentage}] }
  const [saving, setSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  const emptyTxn = () => ({
    _key: `txn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    client_id: '', client_type_flag: 'Existing Client', category: 'Mutual Fund',
    fund_name: '', fund_type: '', transaction_nature: 'Financial',
    transaction_type: 'SIP', amount: '', premium_amount: '', date: '', notes: ''
  });

  const [formRows, setFormRows] = useState([emptyTxn()]);

  const [configForm, setConfigForm] = useState({
    call_incentive: 10, meeting_incentive: 30, new_client_bonus: 200,
    new_client_sip_threshold: 5000, new_client_equity_lumpsum_threshold: 50000,
    purchase_incentive_percent: 0.3,
    sip_slabs: [],
  });

  const fetchData = async () => {
    try {
      const uid = selectedUser || (isAdmin ? undefined : user?.id);
      const [txnRes, summaryRes, configRes, clientsRes, usersRes] = await Promise.all([
        getIncentiveTransactions({ user_id: uid, month: selectedMonth }),
        getIncentiveSummary({ user_id: uid, month: selectedMonth }),
        getIncentiveConfig(),
        getClients(),
        getUsers()
      ]);
      setTransactions(txnRes.data);
      setSummary(summaryRes.data);
      setConfig(configRes.data);
      setClients(clientsRes.data);
      setAllUsers(usersRes.data);
      setConfigForm({
        call_incentive: configRes.data.call_incentive,
        meeting_incentive: configRes.data.meeting_incentive,
        new_client_bonus: configRes.data.new_client_bonus,
        new_client_sip_threshold: configRes.data.new_client_sip_threshold,
        new_client_equity_lumpsum_threshold: configRes.data.new_client_equity_lumpsum_threshold,
        purchase_incentive_percent: configRes.data.purchase_incentive_percent ?? 0.3,
        sip_slabs: (configRes.data.sip_slabs || []).map((s, idx) => ({ ...s, _key: s._key || `slab-${idx}-${s.min}` })),
      });
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [selectedUser, selectedMonth]);

  const handleAdd = async () => {
    // Validate all rows
    for (let i = 0; i < formRows.length; i++) {
      const row = formRows[i];
      if (!row.client_id) { toast.error(`Row ${i + 1}: Please select a client`); return; }
      if (!row.date) { toast.error(`Row ${i + 1}: Please select a date`); return; }
    }
    setSaving(true);
    try {
      let successCount = 0;
      for (const form of formRows) {
        const payload = { ...form };
        if (form.category !== 'Mutual Fund') {
          payload.fund_name = null; payload.fund_type = null;
          payload.transaction_nature = null; payload.transaction_type = null;
        }
        if (form.category === 'Mutual Fund') payload.premium_amount = null;
        payload.amount = parseFloat(form.amount) || 0;
        payload.premium_amount = form.premium_amount ? parseFloat(form.premium_amount) : null;
        await createIncentiveTransaction(payload);
        successCount++;
      }
      toast.success(`${successCount} transaction${successCount > 1 ? 's' : ''} added`);
      setShowAddModal(false);
      setFormRows([emptyTxn()]);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally { setSaving(false); }
  };

  const updateFormRow = (index, field, value) => {
    setFormRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const addFormRow = () => setFormRows(prev => [...prev, emptyTxn()]);
  const removeFormRow = (index) => {
    if (formRows.length <= 1) return;
    setFormRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleStatusChange = async (txnId, newStatus) => {
    try {
      await updateIncentiveTransaction(txnId, { incentive_status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDeleteTxn = async (txnId) => {
    if (!window.confirm('Delete this incentive entry?')) return;
    try {
      await deleteIncentiveTransaction(txnId);
      toast.success('Entry deleted');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const openSplit = (txn) => {
    const existing = (txn.splits || []).map(s => ({ _key: `sp-${s.user_id}`, user_id: s.user_id, percentage: s.percentage }));
    setSplittingTxn({
      ...txn,
      splitRows: existing.length > 0 ? existing : [{ _key: `sp-${Date.now()}`, user_id: '', percentage: '' }],
    });
  };

  const addSplitRow = () => {
    setSplittingTxn(p => ({ ...p, splitRows: [...(p.splitRows || []), { _key: `sp-${Date.now()}-${Math.random().toString(36).slice(2,6)}`, user_id: '', percentage: '' }] }));
  };
  const removeSplitRow = (idx) => {
    setSplittingTxn(p => ({ ...p, splitRows: p.splitRows.filter((_, i) => i !== idx) }));
  };
  const updateSplitRow = (idx, field, value) => {
    setSplittingTxn(p => ({ ...p, splitRows: p.splitRows.map((r, i) => i === idx ? { ...r, [field]: value } : r) }));
  };

  const handleApplySplit = async () => {
    if (!splittingTxn) return;
    const rows = splittingTxn.splitRows || [];
    const cleaned = rows.filter(r => r.user_id && r.percentage);
    const total = cleaned.reduce((acc, r) => acc + (parseFloat(r.percentage) || 0), 0);
    if (total > 100.0001) {
      toast.error(`Total split is ${total}% — must be ≤ 100%`);
      return;
    }
    for (const r of cleaned) {
      if (r.user_id === splittingTxn.created_by) {
        toast.error('Cannot split to the transaction owner; they keep the remainder automatically.');
        return;
      }
    }
    setSaving(true);
    try {
      const payload = cleaned.map(r => ({ user_id: r.user_id, percentage: parseFloat(r.percentage) }));
      await splitIncentiveTransaction(splittingTxn.id, payload);
      toast.success(payload.length === 0 ? 'Splits cleared' : 'Split applied');
      setSplittingTxn(null);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally { setSaving(false); }
  };

  const handleEditSave = async () => {
    if (!editingTxn) return;
    setSaving(true);
    try {
      const payload = {};
      for (const key of ['client_type_flag', 'category', 'fund_name', 'fund_type', 'transaction_nature', 'transaction_type', 'amount', 'premium_amount', 'date', 'notes', 'incentive_status', 'incentive_override']) {
        if (editingTxn[key] !== undefined) payload[key] = editingTxn[key];
      }
      if (payload.amount) payload.amount = parseFloat(payload.amount) || 0;
      if (payload.premium_amount) payload.premium_amount = parseFloat(payload.premium_amount) || null;
      if (payload.incentive_override !== undefined && payload.incentive_override !== '') {
        payload.incentive_override = parseFloat(payload.incentive_override);
      } else {
        delete payload.incentive_override;
      }
      await updateIncentiveTransaction(editingTxn.id, payload);
      toast.success('Entry updated');
      setEditingTxn(null);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally { setSaving(false); }
  };




  const updateSlab = (index, field, value) => {
    setConfigForm(p => ({
      ...p,
      sip_slabs: p.sip_slabs.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const addSlab = () => {
    setConfigForm(p => {
      const lastSlab = p.sip_slabs[p.sip_slabs.length - 1];
      const newMin = lastSlab ? (parseFloat(lastSlab.max) || 0) + 1 : 0;
      return { ...p, sip_slabs: [...p.sip_slabs, { _key: `slab-${Date.now()}-${Math.random().toString(36).slice(2,6)}`, min: newMin, max: newMin + 49999, amount: 0 }] };
    });
  };

  const removeSlab = (index) => {
    if (configForm.sip_slabs.length <= 1) return;
    setConfigForm(p => ({ ...p, sip_slabs: p.sip_slabs.filter((_, i) => i !== index) }));
  };


  const handleConfigSave = async () => {
    setSaving(true);
    try {
      const slabs = configForm.sip_slabs.map((s, i, arr) => ({
        min: parseFloat(s.min) || 0,
        max: i < arr.length - 1 ? (parseFloat(s.max) || 0) : 999999999,
        amount: parseFloat(s.amount) || 0,
      }));
      await updateIncentiveConfig({
        call_incentive: parseFloat(configForm.call_incentive),
        meeting_incentive: parseFloat(configForm.meeting_incentive),
        new_client_bonus: parseFloat(configForm.new_client_bonus),
        new_client_sip_threshold: parseFloat(configForm.new_client_sip_threshold),
        new_client_equity_lumpsum_threshold: parseFloat(configForm.new_client_equity_lumpsum_threshold),
        purchase_incentive_percent: parseFloat(configForm.purchase_incentive_percent),
        sip_slabs: slabs,
      });
      toast.success('Config updated');
      setShowConfigModal(false);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-[500px]" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="incentives-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Incentives</h1>
          <p className="text-sm text-zinc-500 mt-1">Track transactions, targets & earnings</p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <Button variant="outline" onClick={() => setShowConfigModal(true)} className="rounded-sm" data-testid="incentive-config-btn">
              Configure Rules
            </Button>
          )}
          {!isAdmin && (
            <Button onClick={() => setShowAddModal(true)} className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm" data-testid="add-incentive-btn">
              <Plus size={16} className="mr-2" /> Add Transaction
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 whitespace-nowrap">Month</Label>
          <Input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="w-[160px] rounded-sm" data-testid="incentive-month-filter" />
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 whitespace-nowrap">Employee</Label>
            <SearchableSelect
              value={selectedUser}
              onValueChange={setSelectedUser}
              options={[{ value: '', label: 'All' }, ...allUsers.filter(u => u.status === 'active' || u.id).map(u => ({ value: u.id, label: u.name }))]}
              placeholder="All employees"
              triggerClassName="w-[200px]"
              data-testid="incentive-user-filter"
            />
          </div>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchData()}
          disabled={loading}
          className="rounded-sm ml-auto"
          data-testid="incentive-refresh-btn"
        >
          <ArrowClockwise size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-zinc-200 rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendUp size={16} className="text-[#002FA7]" />
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Quarterly SIP Total</p>
              </div>
              <p className="text-2xl font-bold tabular-nums">{formatCurrency(summary.quarterly_new_sip || summary.total_new_sip)}</p>
              <p className="text-xs text-zinc-400 mt-1">This month: {formatCurrency(summary.monthly_new_sip || 0)}</p>
              <p className="text-xs text-zinc-400">SIP slab incentive: {formatCurrency(summary.sip_incentive)}</p>
              {summary.policy_start_date && (
                <p className="text-[10px] text-zinc-300 mt-1">Policy: {summary.policy_start_date} to {summary.policy_end_date}</p>
              )}
            </CardContent>
          </Card>
          <Card className="border-zinc-200 rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target size={16} className="text-emerald-600" />
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Total Incentive</p>
              </div>
              <p className="text-2xl font-bold tabular-nums text-emerald-600">{formatCurrency(summary.total_calculated_incentive)}</p>
              <div className="text-xs text-zinc-400 mt-1 space-y-0.5">
                <p>New client bonus: {formatCurrency(summary.new_client_bonus_total)} ({summary.new_client_bonus_count} clients)</p>
                {summary.purchase_incentive > 0 && <p>Purchase / Broker Change: {formatCurrency(summary.purchase_incentive)}</p>}
              </div>
            </CardContent>
          </Card>
          <Card className="border-zinc-200 rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <CurrencyInr size={16} className="text-amber-600" />
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Status Breakdown</p>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-green-600 font-medium">Paid</span>
                  <span className="text-sm font-bold tabular-nums">{formatCurrency(summary.paid_amount || 0)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-blue-600 font-medium">Approved</span>
                  <span className="text-sm font-bold tabular-nums">{formatCurrency(summary.approved_amount)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-amber-600 font-medium">{isAdmin ? 'Pending' : 'Pending Processing'}</span>
                  <span className="text-sm font-bold tabular-nums">{formatCurrency(summary.pending_amount)}</span>
                </div>
                {summary.total_paid_all_time != null && (
                  <div className="flex items-center justify-between border-t border-zinc-100 pt-1.5 mt-1">
                    <span className="text-xs text-zinc-600 font-semibold">Total Paid (All Time)</span>
                    <span className="text-sm font-bold tabular-nums text-emerald-700">{formatCurrency(summary.total_paid_all_time)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          <Card className={`rounded-sm ${summary.shortfall_to_next_slab > 0 ? 'border-red-300 bg-red-50/30' : 'border-zinc-200 bg-blue-50/20 border-[#002FA7]/20'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                {summary.shortfall_to_next_slab > 0 ? <Warning size={16} className="text-red-500" /> : <Target size={16} className="text-[#002FA7]" />}
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Target & Shortfall</p>
              </div>
              {summary.shortfall_to_next_slab > 0 ? (
                <>
                  <p className="text-2xl font-bold tabular-nums text-red-600">{formatCurrency(summary.shortfall_to_next_slab)}</p>
                  <p className="text-xs text-red-500 mt-1 font-medium">
                    Shortfall to reach {formatCurrency(summary.next_slab_target)}
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    Reach target to earn {formatCurrency(summary.next_slab_reward)}
                  </p>
                </>
              ) : (
                <p className="text-sm text-emerald-600 font-medium">Top slab reached!</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Incentive Calculation Breakdown (Admin view) */}
      {isAdmin && summary && (
        <Card className="border-zinc-200 rounded-sm bg-zinc-50/50">
          <CardHeader className="border-b border-zinc-100 pb-3">
            <CardTitle className="text-base">Incentive Calculation Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center py-1.5 border-b border-zinc-100">
                <span className="text-zinc-600">Quarterly SIP Total</span>
                <span className="font-semibold tabular-nums">{formatCurrency(summary.quarterly_new_sip || summary.total_new_sip)}</span>
              </div>
              <div className="flex justify-between items-center py-1.5 border-b border-zinc-100">
                <span className="text-zinc-600">SIP Slab Incentive</span>
                <span className="font-semibold tabular-nums text-[#002FA7]">{formatCurrency(summary.sip_incentive)}</span>
              </div>
              <div className="flex justify-between items-center py-1.5 border-b border-zinc-100">
                <span className="text-zinc-600">New Client Bonus ({summary.new_client_bonus_count} clients x {formatCurrency(summary.config?.new_client_bonus || 200)})</span>
                <span className="font-semibold tabular-nums text-[#002FA7]">{formatCurrency(summary.new_client_bonus_total)}</span>
              </div>
              <div className="flex justify-between items-center py-1.5 border-b border-zinc-100">
                <span className="text-zinc-600">Purchase Incentive ({summary.config?.purchase_incentive_percent || 0.3}%)</span>
                <span className="font-semibold tabular-nums text-[#002FA7]">{formatCurrency(summary.purchase_incentive)}</span>
              </div>
              <div className="flex justify-between items-center py-1.5 border-b border-zinc-100">
                <span className="text-zinc-600">Activity Incentive (Calls + Meetings)</span>
                <span className="font-semibold tabular-nums text-[#002FA7]">{formatCurrency(summary.activity_incentive)}</span>
              </div>
              {(summary.override_total || 0) > 0 && (
                <div className="flex justify-between items-center py-1.5 border-b border-zinc-100">
                  <span className="text-zinc-600">Admin Overrides</span>
                  <span className="font-semibold tabular-nums text-[#002FA7]">{formatCurrency(summary.override_total)}</span>
                </div>
              )}
              <div className="flex justify-between items-center py-2 bg-[#002FA7]/5 px-3 rounded-sm mt-2">
                <span className="font-semibold text-zinc-800">Total Calculated Incentive</span>
                <span className="font-bold tabular-nums text-lg text-[#002FA7]">{formatCurrency(summary.total_calculated_incentive)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transactions Table */}
      <Card className="border-zinc-200 rounded-sm">
        <CardHeader className="border-b border-zinc-100 pb-3">
          <CardTitle className="text-base">Transactions ({transactions.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {transactions.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">No incentive transactions for this period</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Client</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Type</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Category</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold text-right">Amount</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Date</TableHead>
                    {isAdmin && <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Employee</TableHead>}
                    <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Status</TableHead>
                    <TableHead className="w-[140px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map(txn => {
                    const activityNotes = txn.activity_details?.description || '';
                    const linkedFu = txn.linked_followup;
                    const linkedTk = txn.linked_task;
                    const isActivity = txn.source === 'activity';
                    const displayStatus = (!isAdmin && txn.incentive_status === 'Pending Approval') ? 'Pending Processing' : txn.incentive_status;
                    const hasSplits = (txn.splits || []).length > 0;
                    const isOwner = txn.created_by === user?.id;
                    return (
                    <TableRow key={txn.id} className="hover:bg-zinc-50 cursor-pointer" data-testid={`incentive-txn-${txn.id}`} onClick={() => setViewingTxn(txn)}>
                      <TableCell>
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <p className="font-medium text-sm">{txn.client_name || '—'}</p>
                                  {isActivity && (txn.activity_details?.type === 'Call'
                                    ? <Phone size={12} weight="fill" className="text-zinc-400" />
                                    : <VideoCamera size={12} weight="fill" className="text-zinc-400" />)}
                                  {linkedFu && <CalendarCheck size={12} weight="fill" className="text-amber-500" title={`Follow-up: ${linkedFu.type}`} />}
                                  {linkedTk && <ClipboardText size={12} weight="fill" className="text-emerald-600" title={`Task: ${linkedTk.type}`} />}
                                  {hasSplits && <UsersThree size={12} weight="fill" className="text-purple-500" title="Split" />}
                                </div>
                                <p className="text-[10px] text-zinc-400">{txn.client_type_flag}</p>
                              </div>
                            </TooltipTrigger>
                            {(isActivity && (activityNotes || linkedFu || linkedTk)) && (
                              <TooltipContent side="right" align="start" className="max-w-xs bg-zinc-900 text-white">
                                {activityNotes && <p className="text-xs mb-2"><span className="uppercase tracking-wide text-[10px] text-zinc-400">Notes</span><br/>{activityNotes}</p>}
                                {linkedFu && <p className="text-xs"><span className="uppercase tracking-wide text-[10px] text-amber-300">Follow-up</span><br/>{linkedFu.type} · {linkedFu.due_date}</p>}
                                {linkedTk && <p className="text-xs mt-1"><span className="uppercase tracking-wide text-[10px] text-emerald-300">Task</span><br/>{linkedTk.type} · {linkedTk.due_date}</p>}
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="text-sm">{txn.transaction_type || txn.category}</TableCell>
                      <TableCell>
                        <div title={txn.fund_name || ''}>
                          <p className="text-sm">{txn.category}</p>
                          {txn.fund_name && <p className="text-[10px] text-zinc-500 truncate max-w-[160px]">{txn.fund_name}</p>}
                          {txn.fund_type && !txn.fund_name && <p className="text-[10px] text-zinc-400">{txn.fund_type}</p>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        <div>
                          <p className="font-semibold">{txn.premium_amount ? formatCurrency(txn.premium_amount) : formatCurrency(txn.amount)}</p>
                          {txn.incentive_override != null && (
                            <p className="text-[10px] text-[#002FA7] font-medium">Incentive: {formatCurrency(txn.incentive_override)}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-zinc-600">{txn.date}</TableCell>
                      {isAdmin && <TableCell className="text-sm text-zinc-600">{txn.created_by_name}</TableCell>}
                      <TableCell>
                        <Badge className={`${statusColors[txn.incentive_status] || 'bg-zinc-100 text-zinc-600'} text-xs`} data-testid={`txn-status-${txn.id}`}>
                          {displayStatus}
                        </Badge>
                      </TableCell>
                      {isAdmin && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            {txn.incentive_status === 'Pending Approval' && (
                              <>
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-blue-600" onClick={() => handleStatusChange(txn.id, 'Approved')}>
                                  <Check size={14} className="mr-1" /> Approve
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={() => handleStatusChange(txn.id, 'Rejected')}>
                                  <X size={14} className="mr-1" /> Reject
                                </Button>
                              </>
                            )}
                            {txn.incentive_status === 'Approved' && (
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-green-600" onClick={() => handleStatusChange(txn.id, 'Paid')}>
                                <CurrencyInr size={14} className="mr-1" /> Mark Paid
                              </Button>
                            )}
                            {txn.incentive_status === 'Paid' && (
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={() => handleStatusChange(txn.id, 'Clawback')}>
                                <ArrowClockwise size={14} className="mr-1" /> Clawback
                              </Button>
                            )}
                            {txn.incentive_status !== 'Paid' && txn.incentive_status !== 'Rejected' && txn.incentive_status !== 'Clawback' && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-400 hover:text-purple-600" onClick={() => openSplit(txn)} data-testid={`split-txn-${txn.id}`} title="Split incentive">
                                <ArrowsSplit size={14} />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-400 hover:text-[#002FA7]" onClick={() => setEditingTxn({ ...txn })} data-testid={`edit-txn-${txn.id}`}>
                              <Pencil size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-400 hover:text-red-600" onClick={() => handleDeleteTxn(txn.id)} data-testid={`delete-txn-${txn.id}`}>
                              <Trash size={14} />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                      {!isAdmin && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            {isOwner && txn.incentive_status !== 'Paid' && txn.incentive_status !== 'Rejected' && txn.incentive_status !== 'Clawback' && (
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-purple-600" onClick={() => openSplit(txn)} data-testid={`split-txn-${txn.id}`}>
                                <ArrowsSplit size={14} className="mr-1" /> Split
                              </Button>
                            )}
                            {txn.incentive_status === 'Pending Approval' && isOwner && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-400 hover:text-red-600" onClick={() => handleDeleteTxn(txn.id)}>
                                <Trash size={14} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Transaction Modal (Multi-row) */}
      <Dialog open={showAddModal} onOpenChange={(open) => { if (!open) { setFormRows([emptyTxn()]); } setShowAddModal(open); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Add Incentive Transactions</DialogTitle>
              <Button type="button" variant="outline" size="sm" onClick={addFormRow} className="rounded-sm text-xs" data-testid="add-row-btn">
                <Plus size={14} className="mr-1" /> Add Row
              </Button>
            </div>
            <DialogDescription className="sr-only">Log one or more purchase, SIP, or insurance transactions to compute incentives.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-2 max-h-[65vh] overflow-y-auto">
            {formRows.map((form, idx) => (
              <div key={form._key || idx} className={`space-y-3 ${formRows.length > 1 ? 'p-4 border border-zinc-200 rounded-sm relative' : ''}`}>
                {formRows.length > 1 && (
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-zinc-400">Transaction {idx + 1}</span>
                    <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => removeFormRow(idx)}>
                      <X size={14} />
                    </Button>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase font-semibold">Client <span className="text-red-500">*</span></Label>
                    <SearchableSelect value={form.client_id} onValueChange={(v) => updateFormRow(idx, 'client_id', v)} options={clients.map(c => ({ value: c.id, label: getClientDisplayName(c) }))} placeholder="Select client" searchPlaceholder="Search..." data-testid={`incentive-client-${idx}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase font-semibold">Client Type</Label>
                    <SearchableSelect value={form.client_type_flag} onValueChange={(v) => updateFormRow(idx, 'client_type_flag', v)} options={CLIENT_FLAGS} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase font-semibold">Category</Label>
                    <SearchableSelect value={form.category} onValueChange={(v) => updateFormRow(idx, 'category', v)} options={CATEGORIES} />
                  </div>
                </div>
                {form.category === 'Mutual Fund' && (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase font-semibold">Fund Name</Label>
                        <Input value={form.fund_name} onChange={(e) => updateFormRow(idx, 'fund_name', e.target.value)} className="rounded-sm h-9" placeholder="Scheme name" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase font-semibold">Fund Type</Label>
                        <SearchableSelect value={form.fund_type} onValueChange={(v) => updateFormRow(idx, 'fund_type', v)} options={FUND_TYPES} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase font-semibold">Nature</Label>
                        <SearchableSelect value={form.transaction_nature} onValueChange={(v) => updateFormRow(idx, 'transaction_nature', v)} options={TXN_NATURES} />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {form.transaction_nature === 'Financial' && (
                        <div className="space-y-1">
                          <Label className="text-[10px] uppercase font-semibold">Type</Label>
                          <SearchableSelect value={form.transaction_type} onValueChange={(v) => updateFormRow(idx, 'transaction_type', v)} options={TXN_TYPES} />
                        </div>
                      )}
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase font-semibold">Amount</Label>
                        <Input type="number" min="0" value={form.amount} onChange={(e) => updateFormRow(idx, 'amount', e.target.value)} className="rounded-sm h-9" placeholder="0" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase font-semibold">Date <span className="text-red-500">*</span></Label>
                        <DatePicker value={form.date} onChange={(v) => updateFormRow(idx, 'date', v)} placeholder="Select date" />
                      </div>
                    </div>
                  </>
                )}
                {(form.category === 'Health Insurance' || form.category === 'Term Insurance') && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase font-semibold">Premium (excl. taxes)</Label>
                      <Input type="number" min="0" value={form.premium_amount} onChange={(e) => updateFormRow(idx, 'premium_amount', e.target.value)} className="rounded-sm h-9" placeholder="0" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase font-semibold">Date <span className="text-red-500">*</span></Label>
                      <DatePicker value={form.date} onChange={(v) => updateFormRow(idx, 'date', v)} placeholder="Select date" />
                    </div>
                  </div>
                )}
                {form.category === 'Mutual Fund' && (
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase font-semibold">Notes</Label>
                    <Input value={form.notes} onChange={(e) => updateFormRow(idx, 'notes', e.target.value)} className="rounded-sm h-9" placeholder="Optional" />
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddModal(false); setFormRows([emptyTxn()]); }}>Cancel</Button>
            <Button onClick={handleAdd} disabled={saving} className="bg-[#002FA7] text-white" data-testid="incentive-submit-btn">
              {saving ? 'Adding...' : `Add ${formRows.length} Transaction${formRows.length > 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Modal (Admin) */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Incentive Configuration</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold">Call Incentive (per answered call)</Label>
                <Input type="number" value={configForm.call_incentive} onChange={(e) => setConfigForm(p => ({ ...p, call_incentive: e.target.value }))} className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold">Meeting Incentive (per meeting met)</Label>
                <Input type="number" value={configForm.meeting_incentive} onChange={(e) => setConfigForm(p => ({ ...p, meeting_incentive: e.target.value }))} className="rounded-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold">New Client Bonus</Label>
                <Input type="number" value={configForm.new_client_bonus} onChange={(e) => setConfigForm(p => ({ ...p, new_client_bonus: e.target.value }))} className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold">New Client SIP Threshold</Label>
                <Input type="number" value={configForm.new_client_sip_threshold} onChange={(e) => setConfigForm(p => ({ ...p, new_client_sip_threshold: e.target.value }))} className="rounded-sm" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase font-semibold">New Client Equity Lumpsum Threshold</Label>
              <Input type="number" value={configForm.new_client_equity_lumpsum_threshold} onChange={(e) => setConfigForm(p => ({ ...p, new_client_equity_lumpsum_threshold: e.target.value }))} className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase font-semibold">Purchase Incentive (%)</Label>
              <Input type="number" step="0.01" value={configForm.purchase_incentive_percent} onChange={(e) => setConfigForm(p => ({ ...p, purchase_incentive_percent: e.target.value }))} className="rounded-sm" />
              <p className="text-[10px] text-zinc-400">Percentage of purchase amount given as incentive (e.g. 0.3 = 0.3%)</p>
            </div>
            <div className="bg-zinc-50 p-4 rounded-sm space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">SIP Target Slabs (Monthly)</p>
                <Button type="button" variant="outline" size="sm" onClick={addSlab} className="h-7 text-xs rounded-sm" data-testid="add-slab-btn">
                  <Plus size={12} className="mr-1" /> Add Slab
                </Button>
              </div>
              <div className="space-y-2">
                {configForm.sip_slabs.map((slab, i) => (
                  <div key={slab._key || i} className="flex items-center gap-2" data-testid={`slab-row-${i}`}>
                    <div className="flex-1">
                      <Input
                        type="number" min="0" placeholder="Min"
                        value={slab.min}
                        onChange={(e) => updateSlab(i, 'min', e.target.value)}
                        className="rounded-sm h-8 text-sm"
                      />
                    </div>
                    <span className="text-zinc-400 text-xs">to</span>
                    <div className="flex-1">
                      <Input
                        type="number" min="0" placeholder="Max"
                        value={slab.max >= 999999999 ? '' : slab.max}
                        onChange={(e) => updateSlab(i, 'max', e.target.value || 999999999)}
                        className="rounded-sm h-8 text-sm"
                        disabled={i === configForm.sip_slabs.length - 1}
                      />
                      {i === configForm.sip_slabs.length - 1 && <p className="text-[10px] text-zinc-400 mt-0.5">& above</p>}
                    </div>
                    <span className="text-zinc-400 text-xs">=</span>
                    <div className="flex-1">
                      <Input
                        type="number" min="0" placeholder="Incentive"
                        value={slab.amount}
                        onChange={(e) => updateSlab(i, 'amount', e.target.value)}
                        className="rounded-sm h-8 text-sm font-semibold"
                      />
                    </div>
                    <Button
                      type="button" variant="ghost" size="sm"
                      className="h-8 w-8 p-0 text-red-400 hover:text-red-600"
                      onClick={() => removeSlab(i)}
                      disabled={configForm.sip_slabs.length <= 1}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>Cancel</Button>
            <Button onClick={handleConfigSave} disabled={saving} className="bg-[#002FA7] text-white">
              {saving ? 'Saving...' : 'Save Config'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Transaction Detail Dialog */}
      <Dialog open={!!viewingTxn} onOpenChange={() => setViewingTxn(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">
              {viewingTxn?.source === 'activity' ? 'Activity Incentive Details' : 'Transaction Details'}
            </DialogTitle>
          </DialogHeader>
          {viewingTxn && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Client</p>
                  <p className="text-sm font-medium mt-0.5">{viewingTxn.client_name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Client Type</p>
                  <p className="text-sm mt-0.5">{viewingTxn.client_type_flag}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Category</p>
                  <p className="text-sm mt-0.5">{viewingTxn.category}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Status</p>
                  <Badge className={`${statusColors[viewingTxn.incentive_status] || 'bg-zinc-100'} text-xs mt-0.5`}>{viewingTxn.incentive_status}</Badge>
                </div>
                {viewingTxn.transaction_type && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Transaction Type</p>
                    <p className="text-sm mt-0.5">{viewingTxn.transaction_type}</p>
                  </div>
                )}
                {viewingTxn.fund_name && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Fund Name</p>
                    <p className="text-sm mt-0.5">{viewingTxn.fund_name}</p>
                  </div>
                )}
                {viewingTxn.fund_type && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Fund Type</p>
                    <p className="text-sm mt-0.5">{viewingTxn.fund_type}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Amount</p>
                  <p className="text-sm font-semibold mt-0.5">{formatCurrency(viewingTxn.amount)}</p>
                </div>
                {viewingTxn.incentive_override != null && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-[#002FA7]">Incentive Override</p>
                    <p className="text-sm font-semibold mt-0.5 text-[#002FA7]">{formatCurrency(viewingTxn.incentive_override)}</p>
                  </div>
                )}
                {viewingTxn.premium_amount && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Premium (excl. tax)</p>
                    <p className="text-sm font-semibold mt-0.5">{formatCurrency(viewingTxn.premium_amount)}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Date</p>
                  <p className="text-sm mt-0.5">{viewingTxn.date}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Created By</p>
                  <p className="text-sm mt-0.5">{viewingTxn.created_by_name || '—'}</p>
                </div>
              </div>

              {/* Activity Details (for call/meeting incentives) */}
              {viewingTxn.activity_details && (
                <div className="border-t border-zinc-100 pt-3 mt-3 space-y-3">
                  <div className="flex items-center gap-2">
                    {viewingTxn.activity_details.type === 'Call' ? <Phone size={16} className="text-[#002FA7]" /> : <VideoCamera size={16} className="text-[#002FA7]" />}
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Activity Details</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 bg-zinc-50 p-3 rounded-sm">
                    <div>
                      <p className="text-[10px] uppercase text-zinc-400">Type</p>
                      <p className="text-sm font-medium">{viewingTxn.activity_details.type}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-zinc-400">Status</p>
                      <p className="text-sm font-medium">{viewingTxn.activity_details.status}</p>
                    </div>
                    {viewingTxn.activity_details.duration && (
                      <div>
                        <p className="text-[10px] uppercase text-zinc-400">Duration</p>
                        <p className="text-sm">{viewingTxn.activity_details.duration}</p>
                      </div>
                    )}
                    {viewingTxn.activity_details.time && (
                      <div>
                        <p className="text-[10px] uppercase text-zinc-400">Time</p>
                        <p className="text-sm">{viewingTxn.activity_details.time}</p>
                      </div>
                    )}
                    {viewingTxn.activity_details.date && (
                      <div>
                        <p className="text-[10px] uppercase text-zinc-400">Date</p>
                        <p className="text-sm">{viewingTxn.activity_details.date}</p>
                      </div>
                    )}
                  </div>
                  {viewingTxn.activity_details.description && (
                    <div>
                      <p className="text-[10px] uppercase text-zinc-400">Notes / Description</p>
                      <p className="text-sm text-zinc-700 mt-1 whitespace-pre-wrap">{viewingTxn.activity_details.description}</p>
                    </div>
                  )}
                </div>
              )}

              {viewingTxn.notes && !viewingTxn.activity_details && (
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Notes</p>
                  <p className="text-sm mt-1 text-zinc-700">{viewingTxn.notes}</p>
                </div>
              )}

              {/* Linked Follow-up / Task from this activity */}
              {(viewingTxn.linked_followup || viewingTxn.linked_task) && (
                <div className="border-t border-zinc-100 pt-3 space-y-2">
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Created from this Activity</p>
                  {viewingTxn.linked_followup && (
                    <div className="flex items-center gap-2 bg-blue-50 p-2 rounded-sm">
                      <CalendarCheck size={14} className="text-blue-600" />
                      <span className="text-sm">Follow-up: <strong>{viewingTxn.linked_followup.type}</strong> — Due {viewingTxn.linked_followup.due_date}</span>
                    </div>
                  )}
                  {viewingTxn.linked_task && (
                    <div className="flex items-center gap-2 bg-green-50 p-2 rounded-sm">
                      <ClipboardText size={14} className="text-green-600" />
                      <span className="text-sm">Task: <strong>{viewingTxn.linked_task.type}</strong> — Due {viewingTxn.linked_task.due_date}</span>
                    </div>
                  )}
                </div>
              )}
              {viewingTxn.source === 'activity' && !viewingTxn.linked_followup && !viewingTxn.linked_task && (
                <div className="border-t border-zinc-100 pt-3">
                  <p className="text-xs text-zinc-400 italic">No follow-up or task was created from this activity</p>
                </div>
              )}

              {(viewingTxn.splits || []).length > 0 && (
                <div className="border-t border-zinc-100 pt-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <UsersThree size={14} className="text-purple-500" />
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Split with Teammates</p>
                  </div>
                  <div className="space-y-1">
                    {viewingTxn.splits.map((s, i) => (
                      <div key={`${s.user_id}-${i}`} className="flex items-center justify-between bg-purple-50 p-2 rounded-sm text-sm">
                        <span>{s.user_name || s.user_id}</span>
                        <span className="font-semibold tabular-nums">{s.percentage}%</span>
                      </div>
                    ))}
                    {(() => {
                      const total = viewingTxn.splits.reduce((a, s) => a + (s.percentage || 0), 0);
                      const remainder = Math.max(0, 100 - total);
                      return (
                        <div className="flex items-center justify-between bg-zinc-100 p-2 rounded-sm text-sm">
                          <span className="font-medium">{viewingTxn.created_by_name} (owner)</span>
                          <span className="font-semibold tabular-nums">{remainder.toFixed(2)}%</span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin Edit Transaction Dialog */}
      <Dialog open={!!editingTxn} onOpenChange={() => setEditingTxn(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit Incentive Entry</DialogTitle></DialogHeader>
          {editingTxn && (
            <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold">Client Type</Label>
                  <SearchableSelect value={editingTxn.client_type_flag} onValueChange={(v) => setEditingTxn(p => ({ ...p, client_type_flag: v }))} options={CLIENT_FLAGS} />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold">Status</Label>
                  <SearchableSelect value={editingTxn.incentive_status} onValueChange={(v) => setEditingTxn(p => ({ ...p, incentive_status: v }))} options={STATUSES} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold">Category</Label>
                  <SearchableSelect value={editingTxn.category} onValueChange={(v) => setEditingTxn(p => ({ ...p, category: v }))} options={CATEGORIES} />
                </div>
                {editingTxn.category === 'Mutual Fund' && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase font-semibold">Fund Type</Label>
                    <SearchableSelect value={editingTxn.fund_type || ''} onValueChange={(v) => setEditingTxn(p => ({ ...p, fund_type: v }))} options={FUND_TYPES} />
                  </div>
                )}
              </div>
              {editingTxn.category === 'Mutual Fund' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase font-semibold">Transaction Type</Label>
                    <SearchableSelect value={editingTxn.transaction_type || ''} onValueChange={(v) => setEditingTxn(p => ({ ...p, transaction_type: v }))} options={TXN_TYPES} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase font-semibold">Amount</Label>
                    <Input type="number" value={editingTxn.amount} onChange={(e) => setEditingTxn(p => ({ ...p, amount: e.target.value }))} className="rounded-sm" />
                  </div>
                </div>
              )}
              {(editingTxn.category === 'Health Insurance' || editingTxn.category === 'Term Insurance') && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold">Premium Amount (excl. taxes)</Label>
                  <Input type="number" value={editingTxn.premium_amount || ''} onChange={(e) => setEditingTxn(p => ({ ...p, premium_amount: e.target.value }))} className="rounded-sm" />
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold">Notes</Label>
                <Input value={editingTxn.notes || ''} onChange={(e) => setEditingTxn(p => ({ ...p, notes: e.target.value }))} className="rounded-sm" />
              </div>
              <div className="space-y-2 border-t border-zinc-200 pt-3">
                <Label className="text-xs uppercase font-semibold text-[#002FA7]">Incentive Amount Override</Label>
                <Input type="number" min="0" value={editingTxn.incentive_override ?? ''} onChange={(e) => setEditingTxn(p => ({ ...p, incentive_override: e.target.value }))} className="rounded-sm border-[#002FA7]/30" placeholder="Leave empty for auto-calculation" />
                <p className="text-[10px] text-zinc-400">Set a custom incentive amount for this transaction. Leave empty to use auto-calculated value.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTxn(null)}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={saving} className="bg-[#002FA7] text-white" data-testid="edit-txn-save">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split Dialog */}
      <Dialog open={!!splittingTxn} onOpenChange={(open) => { if (!open) setSplittingTxn(null); }}>
        <DialogContent className="max-w-lg" data-testid="split-dialog">
          <DialogHeader>
            <DialogTitle>Split Incentive</DialogTitle>
            <DialogDescription>
              Allocate a percentage of this incentive to one or more teammates. Remainder stays with the owner.
              Admin users are not eligible to receive splits.
            </DialogDescription>
          </DialogHeader>
          {splittingTxn && (() => {
            const rows = splittingTxn.splitRows || [];
            const totalPct = rows.reduce((a, r) => a + (parseFloat(r.percentage) || 0), 0);
            const remainderPct = Math.max(0, 100 - totalPct);
            const ownerName = splittingTxn.created_by_name || 'Owner';
            const eligibleUsers = allUsers.filter(u =>
              u.id !== splittingTxn.created_by &&
              !((u.roles || []).includes('admin') || u.role === 'admin') &&
              (u.status === 'active' || u.id)
            );
            const baseAmount = splittingTxn.incentive_override != null ? splittingTxn.incentive_override : splittingTxn.amount;
            return (
              <div className="space-y-3">
                <div className="p-3 bg-zinc-50 rounded-sm text-xs space-y-1">
                  <p className="text-zinc-500 uppercase tracking-wide">Transaction</p>
                  <p className="font-medium">{splittingTxn.client_name} · {splittingTxn.transaction_type || splittingTxn.category} · {formatCurrency(baseAmount)}</p>
                </div>
                {rows.map((row, idx) => (
                  <div key={row._key || idx} className="flex gap-2 items-start" data-testid={`split-row-${idx}`}>
                    <div className="flex-1">
                      <SearchableSelect
                        value={row.user_id}
                        onValueChange={(v) => updateSplitRow(idx, 'user_id', v)}
                        options={eligibleUsers.map(u => ({ value: u.id, label: u.name }))}
                        placeholder="Select employee"
                        data-testid={`split-user-${idx}`}
                      />
                    </div>
                    <div className="w-28">
                      <Input
                        type="number"
                        min="0.01" max="100" step="0.01"
                        value={row.percentage}
                        onChange={(e) => updateSplitRow(idx, 'percentage', e.target.value)}
                        placeholder="%"
                        className="rounded-sm"
                        data-testid={`split-pct-${idx}`}
                      />
                    </div>
                    <Button variant="ghost" size="sm" className="h-9 w-9 p-0 text-red-500" onClick={() => removeSplitRow(idx)} disabled={rows.length === 1}>
                      <Trash size={14} />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="rounded-sm" onClick={addSplitRow} data-testid="split-add-row">
                  <Plus size={14} className="mr-1" /> Add teammate
                </Button>
                <div className={`p-3 rounded-sm text-xs flex items-center justify-between border ${totalPct > 100 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-100 text-[#002FA7]'}`}>
                  <span>Team total: <strong>{totalPct.toFixed(2)}%</strong></span>
                  <span>{ownerName} keeps: <strong>{remainderPct.toFixed(2)}%</strong></span>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSplittingTxn(null)}>Cancel</Button>
            <Button onClick={handleApplySplit} disabled={saving} className="bg-[#002FA7] text-white" data-testid="split-save">
              {saving ? 'Saving...' : 'Apply Split'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
